import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { apiRequest } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import {
  Eye,
  Search,
  RefreshCw,
  Check,
  Coins,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface Transaction {
  reference: string;
  userId: number;
  amount: number;
  coins: number;
  status: 'success' | 'pending' | 'failed';
  createdAt: string;
  updatedAt?: string;
  email?: string;
  message?: string;
  gatewayResponse?: any;
}

export function TransactionsTab() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedVerifyRef, setSelectedVerifyRef] = useState<string>("");

  // Query to get all transactions
  const {
    data: transactions,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin-transactions"],
    queryFn: async () => {
      const response = await apiRequest("/api/currency/admin-transactions");
      const data = response as { data: Transaction[] };
      return data.data;
    },
  });

  // Function to load transaction details
  const fetchTransactionDetails = async (reference: string) => {
    try {
      const response = await apiRequest(`/api/currency/admin-transactions/${reference}`);
      const data = response as { success: boolean; data: any; message?: string };
      if (data.success) {
        setSelectedTransaction(data.data ?? {});
        setIsDetailModalOpen(true);
      } else {
        throw new Error(data.message ?? 'Failed to fetch transaction details');
      }
    } catch (error) {
      toast({
        title: "Error fetching transaction details",
        description: error instanceof Error ? error.message : "Failed to load transaction details",
        variant: "destructive",
      });
    }
  };

  // Add verification mutation
  const verifyTransactionMutation = useMutation({
    mutationFn: async (reference: string) => {
      const response = await apiRequest(`/api/currency/verify-transaction/${reference}`, {
        method: "POST"
      });
      return response;
    },
    onSuccess: (data: any) => {
      toast({
        title: data.success ? "Transaction verified" : "Verification failed",
        description: data.message ?? (data.success ? "Payment verified and coins added" : "Could not verify payment"),
        variant: data.success ? "default" : "destructive"
      });
      refetch();
      if (data.success) {
        setIsDetailModalOpen(false);
      }
    },
    onError: (error: any) => {
      toast({
        title: "Verification failed",
        description: error.message ?? "Could not verify the transaction",
        variant: "destructive"
      });
    }
  });

  const handleVerifyTransaction = (reference: string) => {
    verifyTransactionMutation.mutate(reference);
  };

  const handleVerifyClick = (reference: string) => {
    setSelectedVerifyRef(reference);
    setVerifyDialogOpen(true);
  };

  const handleVerifyConfirm = () => {
    if (selectedVerifyRef) {
      handleVerifyTransaction(selectedVerifyRef);
      setVerifyDialogOpen(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  // Calculate date ranges for filter
  const getDateRangeFilter = (transaction: Transaction) => {
    const date = new Date(transaction.createdAt);
    const now = new Date();
    const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff < 1) return "today";
    if (daysDiff < 7) return "week";
    if (daysDiff < 30) return "month";
    return "older";
  };

  // Filter transactions based on search query and filters
  const filteredTransactions = Array.isArray(transactions)
    ? transactions.filter((transaction: Transaction) => {
        // Search filter
        const matchesSearch = [
          transaction.reference?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false,
          transaction.userId?.toString().includes(searchQuery) ?? false,
          transaction.email?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false
        ].some(Boolean);

        // Status filter
        const matchesStatus = statusFilter === "all" || transaction.status === statusFilter;

        // Date range filter
        const matchesDateRange =
          dateRangeFilter === "all" || getDateRangeFilter(transaction) === dateRangeFilter;

        return matchesSearch && matchesStatus && matchesDateRange;
      })
    : [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <RefreshCw className="animate-spin h-8 w-8" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-destructive/10 rounded-lg text-center">
        <h3 className="text-lg font-medium">Error loading transactions</h3>
        <p className="text-muted-foreground">{error instanceof Error ? error.message : 'An unknown error occurred'}</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => refetch()}
        >
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between mb-4">
        <h2 className="text-2xl font-semibold">Transaction Management</h2>
      </div>

      {/* Search and filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by reference or user ID..."
              className="pl-9 w-full rounded-md border border-input bg-slate-800 py-2 text-sm text-white ring-offset-background placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="flex gap-2">
          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="success">Success</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
          </select>

          <select
            className="rounded-md border border-input bg-slate-800 px-3 py-2 text-sm text-white ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={dateRangeFilter}
            onChange={(e) => setDateRangeFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="older">Older</option>
          </select>
        </div>
      </div>

      {!transactions || !Array.isArray(transactions) || transactions.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No transactions found
        </div>
      ) : filteredTransactions.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-muted/50">
          No transactions match your search criteria
        </div>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-2">Reference</th>
                <th className="text-left p-2">User ID</th>
                <th className="text-left p-2">Amount</th>
                <th className="text-left p-2">Coins</th>
                <th className="text-left p-2">Date</th>
                <th className="text-left p-2">Status</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.map((transaction) => (
                <tr
                  key={transaction.reference}
                  className="border-t hover:bg-muted/50"
                >
                  <td className="p-2 font-mono text-xs">{transaction.reference}</td>
                  <td className="p-2">{transaction.userId}</td>
                  <td className="p-2">GHS {transaction.amount}</td>
                  <td className="p-2">
                    <span className="flex items-center">
                      <Coins className="h-4 w-4 mr-1 text-yellow-500" />
                      {transaction.coins}
                    </span>
                  </td>
                  <td className="p-2 text-sm">{formatDate(transaction.createdAt)}</td>
                  <td className="p-2">
                    <Badge
                      className={
                        transaction.status === "success"
                          ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                          : transaction.status === "pending"
                            ? "bg-amber-600 hover:bg-amber-700 text-white"
                            : "bg-rose-600 hover:bg-rose-700 text-white"
                      }
                    >
                      {transaction.status}
                    </Badge>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => fetchTransactionDetails(transaction.reference)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        Details
                      </Button>
                      
                      {transaction.status === "pending" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerifyClick(transaction.reference)}
                          disabled={verifyTransactionMutation.isPending}
                        >
                          {verifyTransactionMutation.isPending ? (
                            <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 mr-1" />
                          )}
                          Verify
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Verification Dialog */}
      <AlertDialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Verify Payment</AlertDialogTitle>
            <AlertDialogDescription>
              This will check the payment status with the payment gateway. If successful, coins will be added to the user's account. Do you want to proceed?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              onClick={handleVerifyConfirm}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={verifyTransactionMutation.isPending}
            >
              {verifyTransactionMutation.isPending ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Verify Payment
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Transaction Details Dialog */}
      <Dialog open={isDetailModalOpen} onOpenChange={setIsDetailModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Transaction Details</DialogTitle>
            <DialogDescription>
              Detailed information about this transaction
            </DialogDescription>
          </DialogHeader>
          
          {selectedTransaction && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Reference:</p>
                  <p className="font-mono text-sm">{selectedTransaction.reference}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Status:</p>
                  <Badge
                    className={
                      selectedTransaction.status === "success"
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : selectedTransaction.status === "pending"
                          ? "bg-amber-600 hover:bg-amber-700 text-white"
                          : "bg-rose-600 hover:bg-rose-700 text-white"
                    }
                  >
                    {selectedTransaction.status}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">User ID:</p>
                  <p>{selectedTransaction.userId}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Email:</p>
                  <p>{selectedTransaction.email ?? "Not provided"}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Amount:</p>
                  <p>GHS {selectedTransaction.amount}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Coins:</p>
                  <p className="flex items-center">
                    <Coins className="h-4 w-4 mr-1 text-yellow-500" />
                    {selectedTransaction.coins}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Created At:</p>
                  <p>{formatDate(selectedTransaction.createdAt)}</p>
                </div>
                {selectedTransaction.updatedAt && (
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-muted-foreground">Updated At:</p>
                    <p>{formatDate(selectedTransaction.updatedAt)}</p>
                  </div>
                )}
              </div>

              {selectedTransaction.message && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Message:</p>
                  <p className="text-sm">{selectedTransaction.message}</p>
                </div>
              )}

              {selectedTransaction.gatewayResponse && (
                <div className="space-y-1">
                  <p className="text-sm font-medium text-muted-foreground">Gateway Response:</p>
                  <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
                    {typeof selectedTransaction.gatewayResponse === 'object'
                      ? JSON.stringify(selectedTransaction.gatewayResponse, null, 2)
                      : selectedTransaction.gatewayResponse}
                  </pre>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            {selectedTransaction?.status === "pending" && (
              <Button
                variant="secondary"
                onClick={() => handleVerifyClick(selectedTransaction.reference)}
                disabled={verifyTransactionMutation.isPending}
              >
                {verifyTransactionMutation.isPending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Verify Payment
                  </>
                )}
              </Button>
            )}
            <Button onClick={() => setIsDetailModalOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}